export type LayoutSettingStatus = "live" | "remount" | "unavailable";

export type LayoutResizeHandle =
  | "n"
  | "ne"
  | "e"
  | "se"
  | "s"
  | "sw"
  | "w"
  | "nw";

/** Canonical set of every resize direction react-grid-layout supports. */
export const ALL_LAYOUT_RESIZE_HANDLES: ReadonlyArray<LayoutResizeHandle> = [
  "n",
  "ne",
  "e",
  "se",
  "s",
  "sw",
  "w",
  "nw",
];

export type LayoutSettingControl =
  | {
      kind: "boolean";
    }
  | {
      kind: "number";
      min: number;
      max: number;
      step: number;
      integer: boolean;
    }
  | {
      kind: "text";
    }
  | {
      kind: "select";
      options: ReadonlyArray<{ value: string; label: string }>;
    }
  | {
      kind: "handles";
      options: ReadonlyArray<{
        value: LayoutResizeHandle;
        label: string;
      }>;
    }
  | {
      kind: "breakpoints";
      layoutOptions: ReadonlyArray<{ value: string; label: string }>;
    };

export interface LayoutSettingDescriptor {
  id: string;
  label: string;
  description: string;
  status: LayoutSettingStatus;
  /**
   * Qualifier shown next to the status badge when "Live" alone would
   * overstate the effect — e.g. a setting whose value is stored immediately
   * but only changes geometry on a later, related change.
   */
  statusNote?: string;
  control: LayoutSettingControl;
  unavailableReason?: string;
}

export interface LayoutSettingSectionDescriptor {
  id: string;
  label: string;
  /** Tooltip text shown on the section heading — explains what the section controls. */
  description: string;
  fields: ReadonlyArray<LayoutSettingDescriptor>;
}

export interface BreakpointDraft {
  width: string;
  columns: string;
  layout: string;
}

export type LayoutSettingValue =
  | boolean
  | string
  | ReadonlyArray<string>
  | ReadonlyArray<BreakpointDraft>;

export interface LayoutSettingFieldView {
  descriptor: LayoutSettingDescriptor;
  value: LayoutSettingValue;
  error?: string;
  disabled?: boolean;
  disabledReason?: string;
}

export interface LayoutSettingSectionView {
  id: string;
  label: string;
  /** Tooltip text shown on the section heading — explains what the section controls. */
  description: string;
  fields: ReadonlyArray<LayoutSettingFieldView>;
}

/**
 * `draft` and `breakpoint-change` on a numeric column carry in-progress
 * keystrokes: controllers show them immediately but debounce the engine
 * application. `draft-commit` and `breakpoint-commit` are the explicit
 * "user is done" signals raised by Enter and blur, which bypass the delay.
 * Every other kind is a discrete control and applies immediately.
 */
export type LayoutSettingChange =
  | { kind: "boolean"; value: boolean }
  | { kind: "draft"; value: string }
  | { kind: "draft-commit" }
  | { kind: "select"; value: string }
  | { kind: "handle"; value: string; checked: boolean }
  | { kind: "breakpoint-add" }
  | { kind: "breakpoint-remove"; index: number }
  | {
      kind: "breakpoint-change";
      index: number;
      field: "width" | "columns" | "layout";
      value: string;
    }
  | { kind: "breakpoint-commit" };

export interface LayoutSettingsPanelPresentationProps {
  expanded: boolean;
  panelId: string;
  labelledBy: string;
  sections: ReadonlyArray<LayoutSettingSectionView>;
  onFieldChange: (fieldId: string, change: LayoutSettingChange) => void;
}
